"use client";

import { MoneyChange, TickCircle } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useCallback, useMemo, useState } from "react";
import { Form } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { RHFSelect } from "@/components/ui/hook-form";
import { Separator } from "@/components/ui/separator";
import ContractAddress from "@/contract/addresses";
import { useConvertAsset } from "@/hooks/nft/useConvertAsset";
import { useFetchMyNFTs } from "@/hooks/nft/useFetchMyNFTs";
import { useFetchMultipleNftInfo } from "@/hooks/nft/useFetchNftInfo";
import { useFetchWrappedTokens } from "@/hooks/nft/useFetchWrappedTokens";
import { useFetchWrapTokenList } from "@/hooks/nft/useFetchWrapTokenList";
import { useLongTxHint } from "@/hooks/useLongTxHint";
import { $api } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { getLandTypeByTokenId } from "@/lib/utils/land-utils";
import { makeConvertDialogSchema } from "@/lib/validations/convert";
import type { ConvertDialogForm as ConvertDialogFormType } from "@/lib/validations/types";

interface ConvertDialogFormProps {
  onSuccess?: () => void;
}

export const ConvertDialogForm = ({ onSuccess }: ConvertDialogFormProps) => {
  const t = useTranslations("modals");
  const vt = useTranslations("validation");
  const convertDialogSchema = useMemo(() => makeConvertDialogSchema(vt), [vt]);
  const wrappedTokens = useFetchWrappedTokens();
  const wrapTokenListResults = useFetchWrapTokenList(wrappedTokens.data);
  const projects = $api.useQuery("get", "/project/list");

  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  const userNFTs = useFetchMyNFTs(selectedProject as `0x${string}`);

  const convertAsset = useConvertAsset({
    onSuccess: () => {
      onSuccess?.();
    },
  });

  // Long-tx hint for the convert approve+execute flow.
  useLongTxHint(convertAsset.isPending);

  // Prepare items to fetch NFT info for
  const nftItemsToFetch = useMemo(() => {
    if (!userNFTs.data || !selectedProject) return [];

    return userNFTs.data.map((tokenId: bigint) => ({
      collectionAddress: selectedProject,
      tokenId: Number(tokenId),
    }));
  }, [userNFTs.data, selectedProject]);

  const nftInfoResults = useFetchMultipleNftInfo(nftItemsToFetch);

  const nftOptions = useMemo(() => {
    if (!userNFTs.data?.length || !selectedProject) return [];

    return userNFTs.data.map((tokenId: bigint) => {
      // Find info by matching tokenId and collectionAddress
      const info = nftInfoResults.data?.data?.find(
        (i) =>
          i?.token_id === Number(tokenId) &&
          i?.collectionAddress?.toLowerCase() === selectedProject.toLowerCase(),
      );
      const isLandProject =
        selectedProject.toLowerCase() === ContractAddress.LANDS;
      const type =
        info?.marketType?.plan ??
        (isLandProject ? getLandTypeByTokenId(Number(tokenId)) : null) ??
        t("convert.unknown");
      return {
        label: (
          <div className="flex items-center gap-2">
            <div className="space-x-1">
              <span>NFT</span>
              <span>{t("convert.type")}</span>
              <span className="">{type}</span>
            </div>
            <span className="w-1 h-1 bg-text-primary" />
            <div>{t("convert.nftIdLabel")}</div>
            <div dir="ltr">{tokenId.toString()}</div>
          </div>
        ),
        value: tokenId.toString(),
        collectionAddress: selectedProject,
      };
    });
  }, [userNFTs.data, nftInfoResults.data, selectedProject, t]);

  const wrappedTokenOptions = useMemo(() => {
    if (!wrappedTokens.data?.length || !wrapTokenListResults.data?.length)
      return [];

    return wrappedTokens.data
      .map((wrapperTokenId: bigint) => {
        // Find the corresponding wrapTokenList result for this wrapped token
        const wrapTokenInfo = wrapTokenListResults.data.find(
          (result) => result.id === wrapperTokenId,
        );

        if (!wrapTokenInfo) return null;

        return {
          label: (
            <div className="flex items-center gap-2">
              <div className="space-x-1">
                <span>Wrapper</span>
                <span>{t("convert.type")}</span>
                <span className="">{wrapTokenInfo.plan}</span>
              </div>
              <span className="w-1 h-1 bg-text-primary" />
              <div>{t("convert.wrapperIdLabel")}</div>
              <div dir="ltr">{wrapperTokenId.toString()}</div>
            </div>
          ),
          value: wrapperTokenId.toString(), // Wrapped token ID
          collectionAddress: wrapTokenInfo.collection_address,
          originalTokenId: wrapTokenInfo.original_id.toString(),
        };
      })
      .filter((opt): opt is NonNullable<typeof opt> => opt !== null);
  }, [wrappedTokens.data, wrapTokenListResults.data, t]);

  const handleSubmit = useCallback(
    (data: ConvertDialogFormType) => {
      const { asset, shoudlConvertToNFT, project } = data;
      if (!asset || !project) return;

      const selectedOption = shoudlConvertToNFT
        ? wrappedTokenOptions.find((opt) => opt.value === asset)
        : nftOptions?.find((opt) => opt.value === asset);

      if (!selectedOption) return;

      convertAsset.mutate({
        collectionAddress: project as `0x${string}`,
        tokenId: asset,
        convertToNFT: shoudlConvertToNFT,
        ...(shoudlConvertToNFT && { wrapperTokenId: asset }),
      });
    },
    [nftOptions, wrappedTokenOptions, convertAsset],
  );

  return (
    <Form
      schema={convertDialogSchema}
      onSubmit={handleSubmit}
      defaultValues={{ shoudlConvertToNFT: true }}
      className="space-y-3"
    >
      {(form) => {
        const shouldConvertToNFT = form.watch("shoudlConvertToNFT");
        const options = shouldConvertToNFT ? wrappedTokenOptions : nftOptions;
        // Direction-aware loading: show spinner while the data source that
        // powers the currently selected direction is still fetching.
        const assetSelectLoading = shouldConvertToNFT
          ? wrappedTokens.isLoading || wrapTokenListResults.isLoading
          : userNFTs.isLoading;

        return (
          <>
            <div className="pt-4 space-y-4">
              <div className="space-y-3">
                <p className="text-sm text-text-primary font-semibold">
                  {t("convert.selectProject")}
                </p>
                <div className="flex gap-1">
                  {projects.data?.data?.map((project) => (
                    <Button
                      key={project.id}
                      type="button"
                      variant={
                        selectedProject === project.nft_collection_address
                          ? "faded"
                          : "solid"
                      }
                      colors={
                        selectedProject === project.nft_collection_address
                          ? "primary"
                          : "secondary"
                      }
                      startIcon={
                        selectedProject === project.nft_collection_address ? (
                          <TickCircle variant="Bold" size={20} />
                        ) : null
                      }
                      onClick={() => {
                        setSelectedProject(project.nft_collection_address);
                        form.setValue(
                          "project",
                          project.nft_collection_address!,
                        );
                      }}
                      className={cn(
                        "w-fit py-1 px-2",
                        selectedProject === project.nft_collection_address
                          ? "bg-accent-secondary text-accent-foreground"
                          : "bg-bg-tertiary/60 text-text-primary",
                      )}
                    >
                      {project.name}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-text-primary font-semibold">
                  {t("convert.selectConvertType")}
                </p>
                <div className="flex flex-col gap-3">
                  <Button
                    type="button"
                    variant={shouldConvertToNFT ? "faded" : "solid"}
                    colors={"secondary"}
                    startIcon={
                      !shouldConvertToNFT ? (
                        <TickCircle variant="Bold" size={20} />
                      ) : null
                    }
                    onClick={() => {
                      form.setValue("shoudlConvertToNFT", false);
                      form.setValue("asset", ""); // Reset selection when changing direction
                    }}
                    className="w-full"
                  >
                    {t("convert.nftToPeer")}
                  </Button>
                  <Button
                    type="button"
                    variant={shouldConvertToNFT ? "solid" : "faded"}
                    colors={"secondary"}
                    startIcon={
                      shouldConvertToNFT ? (
                        <TickCircle variant="Bold" size={20} />
                      ) : null
                    }
                    onClick={() => {
                      form.setValue("shoudlConvertToNFT", true);
                      form.setValue("asset", ""); // Reset selection when changing direction
                    }}
                    className="w-full"
                  >
                    {t("convert.peerToNft")}
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-3 flex flex-col items-end">
              <div className="space-y-3 w-full">
                <p className="text-sm text-text-primary font-semibold">
                  {t("convert.selectAsset")}
                </p>
                <RHFSelect
                  containerClass="w-full"
                  name="asset"
                  selectContentClassName="z-[120]"
                  loading={assetSelectLoading}
                  options={
                    options?.map((opt) => ({
                      label: opt.label,
                      value: opt.value,
                    })) || []
                  }
                  emptyText={
                    shouldConvertToNFT
                      ? t("convert.selectPeerPlaceholder")
                      : t("convert.selectNftPlaceholder")
                  }
                />
              </div>
              <Separator />
              <Button
                type="submit"
                className="mt-3 w-full"
                colors={"secondary"}
                startIcon={<MoneyChange size={20} />}
                loading={convertAsset.isPending}
                size="lg"
                disabled={
                  !form.watch("asset") ||
                  options?.length === 0 ||
                  convertAsset.isPending
                }
              >
                {shouldConvertToNFT
                  ? t("convert.convertToNft")
                  : t("convert.convertToPeer")}
              </Button>
            </div>
          </>
        );
      }}
    </Form>
  );
};
